/**
 * Mutation endpoint functions for editor operations.
 *
 * Query functions have been moved to entity definitions in sourse/data/entities/.
 * Only mutation endpoints remain here since they're used by useOptimisticMutation
 * and raw useMutation hooks that need a mutationFn.
 */
import { request } from '@/api/apiUtils';
import type {
    CreateFountainScene,
    CreateScene,
    CreateTextAtom,
    IFountainAct,
    IFountainFragment,
    IFountainScene,
    ResponseType,
    TextAtoms,
    UpdateScene,
} from '@/types';

export const api = {
    // Scene mutations
    createScene: async (projectId: number, data: CreateScene): Promise<ResponseType<IFountainScene>> => {
        return await request(`projects/${projectId}/scenes`, {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    updateScene: async (projectId: number, sceneId: number, data: Partial<UpdateScene>): Promise<ResponseType<IFountainScene>> => {
        return await request(`projects/${projectId}/scenes/${sceneId}`, {
            method: 'PATCH',
            body: JSON.stringify(data),
        });
    },

    deleteScene: async (projectId: number, sceneId: number): Promise<ResponseType<void>> => {
        return await request(`projects/${projectId}/scenes/${sceneId}`, { method: 'DELETE' });
    },

    // Text atom mutations
    createTextAtom: async (projectId: number, data: CreateTextAtom): Promise<ResponseType<TextAtoms>> => {
        return await request(`projects/${projectId}/text-atoms`, {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    updateTextAtom: async (projectId: number, atomId: number, data: Partial<CreateTextAtom>): Promise<ResponseType<TextAtoms>> => {
        return await request(`projects/${projectId}/text-atoms/${atomId}`, {
            method: 'PATCH',
            body: JSON.stringify(data),
        });
    },

    deleteTextAtom: async (projectId: number, atomId: number): Promise<ResponseType<TextAtoms>> => {
        return await request(`projects/${projectId}/text-atoms/${atomId}`, { method: 'DELETE' });
    },

    // Fountain structure mutations
    createFountainAct: async (
        projectId: number,
        data: { episode_id?: number; code?: string; title: string; order?: number },
    ): Promise<ResponseType<IFountainAct>> => {
        return await request(`projects/${projectId}/acts`, {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    createFountainFragment: async (
        projectId: number,
        data: { act_id: number; code?: string; title: string; order?: number },
    ): Promise<ResponseType<IFountainFragment>> => {
        return await request(`projects/${projectId}/fragments`, {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    createFountainScene: async (projectId: number, data: CreateFountainScene): Promise<ResponseType<IFountainScene>> => {
        return await request(`projects/${projectId}/scenes`, {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    updateFountainAct: async (
        projectId: number,
        actId: number,
        data: Partial<{ episode_id: number; code: string; title: string; order: number }>,
    ): Promise<ResponseType<IFountainAct>> => {
        return await request(`projects/${projectId}/acts/${actId}`, {
            method: 'PATCH',
            body: JSON.stringify(data),
        });
    },

    updateFountainFragment: async (
        projectId: number,
        fragmentId: number,
        data: Partial<{ act_id: number; code: string; title: string; order: number }>,
    ): Promise<ResponseType<IFountainFragment>> => {
        return await request(`projects/${projectId}/fragments/${fragmentId}?_method=PATCH`, {
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    deleteFountainAct: async (projectId: number, actId: number): Promise<ResponseType<void>> => {
        return await request(`projects/${projectId}/acts/${actId}`, { method: 'DELETE' });
    },

    deleteFountainFragment: async (projectId: number, fragmentId: number): Promise<ResponseType<void>> => {
        return await request(`projects/${projectId}/fragments/${fragmentId}`, { method: 'DELETE' });
    },
};
